"use client";

import React, { useState, useEffect } from "react";
import {
  Menu,
  X,
  Check,
  Video,
  Mic,
  MessageSquare,
  Bot,
  FileText,
  BarChart2,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router-dom";
import ThemeToggle from "../../components/auth/ThemeToggle";
import { useTheme } from "../../context";
import { GoSun } from "react-icons/go";
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaMoon,
  FaTelegram,
  FaTiktok,
  FaTwitter,
  FaYoutube,
} from "react-icons/fa";
import { PlayCircle } from "lucide-react";
import ShowDemoModal from "./show-demo";
import { useOpenInBrowser } from "./useOpenInBrowser";
import { useNavigate } from "react-router-dom";
import { OPEN_WITH_DEVICE_BROWSER } from "../../utils/env.config";
import { telemetryService } from "../../telemetry/TelemetryService";
import {TelemetryEvents } from "../../telemetry/telemetryEvents";

interface Props {
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "outline";
  size?: "sm" | "md" | "lg";
  [key: string]: any;
}

const Button = ({ children, className, onClick }: Props) => {
  return (
    <button
      className={`p-2 px-3.5 flex items-center gap-1 ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

export default function LateralLanding() {
  const { theme, toggleTheme } = useTheme();
  const isDarkMode = theme === "dark";
  const homeRef = React.createRef<HTMLDivElement>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [pricingPeriod, setPricingPeriod] = useState("monthly");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [modalContent, setModalContent] = useState("login");
  const [activeFaqIndex, setActiveFaqIndex] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState("intro");
  const [showLoginButton, setShowLoginButton] = useState(true);
  const [showDemoModal, setShowDemoModal] = useState(false);

  const features = [
    {
      icon: <Video className="w-8 h-8" />,
      title: "Video Mode",
      description:
        "Practice interviews with your camera and an AI-powered avatar, featuring realistic video animations for an immersive experience.",
    },
    {
      icon: <Mic className="w-8 h-8" />,
      title: "Voice Mode",
      description:
        "Conduct voice-only interviews with AI, perfect for practicing verbal responses without video.",
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Chat Mode",
      description:
        "Engage in text-based mock interviews, ideal for written communication practice with AI-driven responses.",
    },
    {
      icon: <Bot className="w-8 h-8" />,
      title: "Auto & Manual Modes",
      description:
        "Choose Auto Mode for hands-free voice recording or Manual Mode to control recordings by holding the record button.",
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Voice-to-Text Transcription",
      description:
        "View real-time transcriptions of your voice and video interviews, also available in Chat Mode for review.",
    },
    {
      icon: <BarChart2 className="w-8 h-8" />,
      title: "Dashboard & Results",
      description:
        "Access immediate interview results, track total and average scores, and manage your profile with ease.",
    },
  ];

  const faqs = [
    {
      question: "What interview modes are available?",
      answer:
        "Our platform offers three modes: Video Mode for face-to-face practice with an AI avatar, Voice Mode for audio-only interviews, and Chat Mode for text-based practice.",
    },
    {
      question: "How do Auto and Manual modes work?",
      answer:
        "Auto Mode automatically records and sends your voice responses hands-free, while Manual Mode lets you hold a record button to control when to send your answers.",
    },
    {
      question: "Can I see my voice responses as text?",
      answer:
        "Yes, our voice-to-text transcription feature converts your voice and video interview responses into text in real-time, also available for review in Chat Mode.",
    },
    {
      question: "How do I access my interview results?",
      answer:
        "After completing an interview, you get immediate results on your dashboard, including detailed scores, feedback, and the ability to track your progress over time.",
    },
    {
      question: "Is the platform suitable for beginners?",
      answer:
        "Our AI provides tailored feedback for all skill levels, making it easy for beginners to improve their interview skills with guided practice.",
    },
    {
      question: "Can I practice on my phone?",
      answer:
        "Yes, our platform is fully responsive, allowing you to practice interviews seamlessly on desktops, tablets, or phones, anytime, anywhere.",
    },
  ];

  const openModal = (type: string) => {
    setModalContent(type);
    if (type === "login") setShowLoginModal(true);
    if (type === "signup") setShowSignupModal(true);
  };

  const closeModal = () => {
    setShowLoginModal(false);
    setShowSignupModal(false);
  };

  const handleFaqToggle = (index: number) => {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  };

  const sections = [
    { id: "intro", label: "HOME" },
    { id: "features", label: "FEATURES" },
    { id: "faq", label: "FAQ" },
    { id: "support", label: "CONTACT" },
    { id: "about-us", label: "ABOUT US" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      let currentSection = "intro"; // default to intro

      // Get all sections and check which one is currently in view
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section) {
          const rect = section.getBoundingClientRect();
          // Check if section is in viewport (accounting for header height)
          if (rect.top <= 100) {
            currentSection = sections[i].id;
            break;
          }
        }
      }

      setActiveSection(currentSection);
    };

    // Run immediately on mount to set initial state
    handleScroll();

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Cleanup
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const el = homeRef.current;
    if (!el) return;

    const handleScroll = () => {
      setIsHeaderSticky(el.scrollTop > 50);
    };

    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, [homeRef]);

  // Also add a separate effect to handle initial load after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      const handleInitialScroll = () => {
        let currentSection = "intro";

        for (let i = sections.length - 1; i >= 0; i--) {
          const section = document.getElementById(sections[i].id);
          if (section) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= 100) {
              currentSection = sections[i].id;
              break;
            }
          }
        }

        setActiveSection(currentSection);
      };

      handleInitialScroll();
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  // Check local storage for tokens or user ID
  useEffect(() => {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const userId = localStorage.getItem("userId");
    if (token || refreshToken || userId) {
      setShowLoginButton(false);
    } else {
      setShowLoginButton(true);
    }
  }, []);

  // Close modal with ESC key and backdrop click
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showDemoModal) {
        setShowDemoModal(false);
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [showDemoModal]);

  const handleModalBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setShowDemoModal(false);
    }
  };
  const navigate = useNavigate();
  const openInBrowser = useOpenInBrowser();

  const handleGetStarted = (eventType: string | null) => {
    if(eventType == null || eventType == "")
    {
      eventType = "Get Started Button Clicked";
    }

    telemetryService.trackEvent(eventType);
    if(OPEN_WITH_DEVICE_BROWSER)
    {
      const loginUrl = "https://sapienstech.net/interview-setup";
      openInBrowser(loginUrl, () => {
        navigate("/interview-setup");
      });
    }
    else
    {
      navigate("/interview-setup");
    }
  };

  return (
    <div
      id="home"
      ref={homeRef}
      className="min-h-screen max-h-screen custom-scrollbar overflow-y-scroll bg-white dark:bg-[#232226] text-gray-900 dark:text-white transition-all"
    >
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-[1000] w-full  transition-all duration-500 ${isHeaderSticky
          ? "bg-white dark:bg-[#202020] dark:!text-slate-100 text-gray-700 shadow-[0_4px_6px_-1px_rgba(255,255,255,0.1),0_2px_4px_-2px_rgba(255,255,255,0.05)]"
          : "bg-accent-500  text-slate-200  shadow-[0_4px_6px_-1px_rgba(255,255,255,0.1),0_2px_4px_-2px_rgba(255,255,255,0.05)]"
          }`}
      >
        <div className="container mx-auto px-4 sm:px-10 lg:px-14  z-[1000]">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="relative  flex items-center space-x-2">
              <div className="flex items-center justify-center w-14 h-14 p-0 rounded-full overflow-hidden">
                <img
                  src={isHeaderSticky ? "/logo/green-logo.png" : "/logo/white-logo.png"}
                  alt="Sapiens AI Logo"
                  className="w-full h-full object-contain"
                />
              </div>


              <Link
                to="/"
                className="text-lg font-semibold leading-tight transition-transform hover:scale-105"
              >
                Sapiens AI
              </Link>
            </div>
            <div className="hidden lg:flex items-center justify-end flex-1 space-x-8">
              <nav className="flex space-x-6">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block text-sm transition-colors ${activeSection === section.id
                      ? (isHeaderSticky ? "text-gray-700 dark:text-slate-300" : "text-slate-50 font-semibold")
                      :
                      isHeaderSticky
                        ?
                        "text-gray-700  dark:text-slate-300"
                        : "text-slate-100 hover:text-slate-50"
                      } `}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMenuOpen(false);
                      setActiveSection(section.id);
                      const el = document.getElementById(section.id);
                      if (el) {
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                  >
                    {section.label}
                  </a>
                ))}
              </nav>

              <div className="flex text-sm items-center space-x-4">
                {/* {showLoginButton && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-accent-400 hover:text-accent-600 dark:hover:text-accent-400"
                    onClick={() => openModal("login")}
                  >
                    <Link to={"/login"}>LOGIN</Link>
                  </Button>
                )} */}
                
                  <Button
                    onClick={() => handleGetStarted(TelemetryEvents.GET_STARTED_BUTTON_MENU)} 
                    className="flex justify-center items-center bg-[#FB993E] hover:bg-transparent hover:border hover:border-[#FB993E] hover:text-[#FB993E] font-bold text-white text-sm px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                    GET STARTED
                  </Button>

                <div className="pl-2 pt-[2px]  align-center justify-center hidden lg:block">
                  <ThemeToggle />
                </div>
              </div>
            </div>
            <div className=" flex lg:hidden items-center ">
              <button
                className="p-2 text-current font-bold transition-transform"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
              <button
                onClick={toggleTheme}
                className=" p-2 rounded-full  border-gray-300 dark:border-gray-600 "
              >
                <GoSun
                  className="text-white hidden dark:inline cursor-pointer hover:text-opacity-75 active:text-opacity-100"
                  size={20}
                />
                <FaMoon
                  className="text-black inline dark:hidden cursor-pointer hover:text-opacity-75 active:text-opacity-100"
                  size={20}
                />
              </button>
            </div>
          </div>
          {isMenuOpen && (
            <div className="lg:hidden bg-white dark:bg-[#202020] border-t dark:border-slate-700 animate-slide-in-top shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.05)] dark:shadow-[0_4px_6px_-1px_rgba(255,255,255,0.1),0_2px_4px_-2px_rgba(255,255,255,0.05)] z-[1000]">
              <div className="px-4 py-6 space-y-4">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block transition-colors ${activeSection === section.id
                      ? "text-accent-500 font-semibold"
                      : "text-gray-900 dark:text-slate-300"
                      } hover:text-accent-500`}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMenuOpen(false);
                      setActiveSection(section.id);
                      const el = document.getElementById(section.id);
                      if (el) {
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }
                    }}
                  >
                    {section.label}
                  </a>
                ))}
                <div className="flex text-sm flex-col space-y-2 pt-4">
                  {/* <Link to={'/login'} className="w-full">
                    <Button
                      variant="outline"
                      className="border-accent-400 text-accent-500 font-bold hover:text-accent-600 dark:hover:text-accent-400"
                    >
                      LOGIN
                    </Button>
                  </Link> */}
                    <Button 
                      onClick={() => handleGetStarted(TelemetryEvents.GET_STARTED_BUTTON_MENU_MOBILE)} 
                      className="bg-[#0dc0a8] hover:bg-[#0aa895] font-bold text-white hover:text-[#0dc0a8] ">
                      GET STARTED
                    </Button>
                  <div className="pl-2 py-4  items-center hidden lg:block">
                    <ThemeToggle />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section
        id="intro"
        className="relative min-h-screen md:py-8 bg-gradient-to-br from-accent-500/90 via-accent-500 to-accent-600 overflow-hidden top-10"
      >
        <div className="container mx-auto px-4 h-screen flex items-center justify-center">
          <div className="mt-20 md:mt-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center w-full max-w-7xl">
            {/* Text Content */}
            <div className="text-white z-10 text-center md:text-left order-1 md:order-1 ]">
              <h1 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-light mb-6 leading-tight">
                <span className="font-black block text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-2">
                  Master Your Interviews
                </span>
                with AI-Powered Practice
              </h1>
              <p className="text-lg lg:text-xl font-normal mb-8 text-slate-100 leading-relaxed">
                Practice mock interviews in Video, Voice, or Chat modes with
                real-time feedback and AI-driven insights.
              </p>
              <p className="pb-6 font-semibold text-white text-2xl md:text-3xl animate-pulse duration-[10000000s] uppercase tracking-wider 0  py-2 rounded-lg inline-block ">
                FREE, NO PAYMENT, NO CREDIT CARD
              </p>
              <div className="flex gap-4 sm:gap-2 w-full items-center justify-center md:justify-start">
                <Button
                  onClick={() => {
                    setShowDemoModal(true);
                    telemetryService.trackEvent(TelemetryEvents.WATCH_DEMO_BUTTON);
                  }}
                  className="flex items-center font-bold justify-center gap-2 rounded-full bg-[#38CBDD] text-white text-sm px-6 py-3 transition-all duration-300 hover:bg-transparent hover:border hover:border-[#38CBDD] sm:w-auto cursor-pointer"
                >
                  WATCH DEMO
                  <PlayCircle className="w-4 h-4" />
                </Button>
                
                <Button 
                  onClick={() => handleGetStarted(TelemetryEvents.GET_STARTED_BUTTON_HOME)}
                  className="flex items-center font-bold justify-center gap-2 rounded-full bg-[#FB993E] text-white text-sm px-6 py-3 transition-all duration-300 hover:bg-transparent hover:border hover:border-[#FB993E] sm:w-auto cursor-pointer">
                    GET STARTED
                </Button>
              </div>
            </div>

            {/* Image Content */}
            <div className="relative w-full h-[50vh] md:h-auto order-2 md:order-2">
              <div className="relative w-full h-full">
                <img
                  src={
                    isDarkMode
                      ? "/landing/firstpage.png"
                      : "/landing/firstpage.png"
                  }
                  alt="Interview Platform Preview"
                  className="w-full h-full object-contain animate-slide-in-right"
                />
              </div>
            </div>
          </div>
        </div>
        <svg
          className="svg-intro-bottom"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M0,70 C30,130 70,50 100,70 L100,100 0,100 Z"
            fill={isDarkMode ? "#232226" : "white"}
          ></path>
        </svg>
        <svg
          className="svg-intro-bottom-2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M0,70 C30,130 70,50 100,70 L100,100 0,100 Z"
            fill={isDarkMode ? "#232226" : "white"}
            fillOpacity="0.4"
          ></path>
        </svg>
        <svg
          className="svg-intro-bottom-3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M95,0 Q90,90 10,100 L100,100 100,0 Z"
            fill={isDarkMode ? "#232226" : "white"}
            fillOpacity="0.1"
          ></path>
        </svg>
      </section>

      {/* About Sections */}
      <section id="about" className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Excel in Interviews with{" "}
                <span className="font-bold">AI-Driven Practice</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg leading-relaxed">
                Our platform uses advanced AI to simulate real interview
                scenarios, helping you build confidence and improve your skills.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Realistic mock interviews powered by AI
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Instant feedback to refine your responses
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Flexible modes for all skill levels
                </li>
              </ul>
            </div>
            <div className="lg:col-span-3">
              <img
                src="/landing/option_1.png"
                alt="Interview Platform"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 order-2 lg:order-1">
              <img
                src="/landing/updatedthree.png"
                alt="Interview Dashboard"
                className="w-full h-auto"
              />
            </div>
            <div className="lg:col-span-2 order-1 lg:order-2">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Personalized{" "}
                <span className="font-bold">Interview Feedback</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg leading-relaxed">
                Receive immediate, detailed feedback on your performance with
                actionable insights to boost your interview success.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Real-time performance analysis
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Track progress with detailed scores
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Save and review past interviews
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Practice Anytime with{" "}
                <span className="font-bold">Flexible Modes</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6 text-lg leading-relaxed">
                Choose Video, Voice, or Chat modes, with Auto or Manual options,
                to practice interviews in a way that suits you.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Multiple interview formats
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Hands-free or manual recording
                </li>
                <li className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  Accessible on all devices
                </li>
              </ul>
            </div>
            <div className="lg:col-span-3">
              <img
                src="/landing/1.png"
                alt="Interview Modes"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className="py-16 lg:py-24 bg-gray-50 dark:bg-[#201f23] relative"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 relative">
              Powerful Features for Interview Success
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-4 w-12 h-1 bg-cyan-500 rounded-full"></div>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
              Our AI-driven platform offers versatile tools to help you excel in
              interviews, with real-time feedback and personalized insights.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="relative bg-white dark:bg-[#29282e] p-6 sm:p-8 rounded-2xl shadow-md dark:shadow-none border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 group will-change-transform animate-feature-card"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
                <div className="relative w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-full flex items-center justify-center text-accent-500 group-hover:bg-accent-500 group-hover:text-white transition-all duration-300 animate-pulse-once">
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            className="w-full h-32 lg:h-40"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,70 C30,130 70,50 100,70 L100,100 0,100 Z"
              fill={isDarkMode ? "#232226" : "white"}
            />
          </svg>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        className="py-16 lg:py-24 bg-white dark:bg-[#232226] relative"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 relative">
              Frequently Asked Questions
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-4 w-12 h-1 bg-cyan-500 rounded-full"></div>
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
              Get answers to common questions about our AI-powered mock
              interview platform to help you get started with confidence.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {faqs.map((faq, index) => (
              <div
                key={`faq-${index}`}
                className={`faq-item relative h-fit bg-white dark:bg-[#29282e] p-6 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-lg min-h-[60px] ${activeFaqIndex === index ? "shadow-lg row-span-2" : ""
                  }`}
              >
                <button
                  className="w-full flex justify-between items-center text-left focus:outline-none"
                  onClick={() => handleFaqToggle(index)}
                  aria-expanded={activeFaqIndex === index}
                  aria-controls={`faq-content-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${activeFaqIndex === index ? "rotate-180" : ""
                      }`}
                  />
                </button>
                <div
                  className="faq-content transition-all duration-500 ease-in-out"
                  id={`faq-content-${index}`}
                  style={{
                    maxHeight: activeFaqIndex === index ? "2000px" : "0px",
                    overflow: "hidden",
                    visibility: activeFaqIndex === index ? "visible" : "hidden",
                    opacity: activeFaqIndex === index ? 1 : 0,
                    transition:
                      "max-height 0.5s ease-in-out, visibility 0.5s, opacity 0.5s ease-in-out",
                    minHeight: 0,
                  }}
                >
                  <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section
        id="support"
        className="relative mx-auto w-[80%] py-12 lg:py-20 text-white overflow-hidden"
      >
        <div className="container mx-auto px-7 sm:px-10 lg:px-14 relative z-10">
          <div className="grid lg:grid-cols-[50%_30%] justify-between items-center">
            <div>
              <h2 className="text-xl lg:text-2xl font-bold mb-6">
                Support for Your Interview Journey
              </h2>
              <p className="text-base mb-8 text-[#b3f0e8]">
                Our 24/7 support team is here to assist with your mock interview
                setup, feature questions, or technical issues to ensure a
                seamless experience.
              </p>
            </div>
            <Link to="/contact-us" className="w-full sm:w-auto">
              <Button className="flex justify-center font-bold items-center bg-[#FB993E] hover:bg-transparent hover:border hover:border-[#FB993E] text-white text-sm px-6 py-3 rounded-full transition-all duration-300 hover:scale-105 w-full sm:w-auto">
                GET IN TOUCH NOW
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute inset-0">
          <svg
            className="w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M0,90 Q0,100 3,100 L95,96 Q100,96 99,80 L95,25 Q94,15 90,15 L6,0 Q2,0 2,10 Z"
              fill="#0dc0a8"
            />
          </svg>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-16 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center opacity-60">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="text-center"></div>
            ))}
          </div>
        </div>
        <svg
          className="svg-cta-top"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M0,70 C30,130 70,50 100,70 L100,100 0,100 Z"
            fill="#3fbba3"
          ></path>
        </svg>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 relative bg-accent-500 text-white text-center">
        <div className="container mx-auto px-4 flex flex-col items-center sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6 relative">
            Start Practicing Today!
            <div className="absolute left-1/2 transform -translate-x-1/2 mt-4 w-12 h-1 bg-white rounded-full"></div>
          </h2>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto mb-8">
            Join thousands of users mastering their interviews with our
            AI-powered platform. Get instant feedback and track your progress.
          </p>

            <Button
              onClick={() => handleGetStarted(TelemetryEvents.START_YOUR_FIRST_INTERVIEW_BUTTON_MENU_MOBILE)}
              className=" font-bold  bg-[#FB993E] hover:bg-transparent hover:border hover:border-[#FB993E] text-white text-sm px-6 py-2 rounded-full transition-all duration-300 hover:bg-[#38CBDD]  hover:text-white border-white  w-full sm:w-auto ">
              START YOUR FIRST INTERVIEW
            </Button>
        </div>
        <svg
          className="svg-cta-bottom"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d="M0,70 C30,130 70,50 100,70 L100,100 0,100 Z"
            fill={isDarkMode ? "#232226" : "white"}
          ></path>
        </svg>
      </section>

      <section
        id="about-us"
        className="py-16 px-4 sm:px-8 md:px-16 bg-white dark:bg-[#1a1a1a] transition-colors duration-300"
      >
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            About Us
          </h2>
          <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-12">
            We're building a smarter way to prepare for interviews. Our platform
            offers interactive, real-time mock interviews, AI-powered feedback,
            and performance tracking—so you can enter every interview with
            confidence.
          </p>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            <div className="bg-gray-50 dark:bg-[#2a2a2a] p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Real-Time Practice
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Practice mock interviews in real-time with AI or peers to
                sharpen your communication and problem-solving skills.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-[#2a2a2a] p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Smart Feedback
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Receive detailed, actionable feedback on your answers, tone, and
                confidence level—instantly.
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-[#2a2a2a] p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Track Your Progress
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Measure your improvement over time and focus on areas where you
                need the most growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 lg:py-24 bg-white dark:bg-[#232226] text-gray-900 dark:text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold mb-4">Sapians-AI</h3>
              <p className="text-gray-400 mb-6">
                Copyright © 2025 Sapians-AI.
                <br />
                All Rights Reserved.
              </p>
              <div className="flex space-x-4">
                <Link
                  to="https://www.linkedin.com/company/sapiens-ai-tech"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaLinkedin className="w-6 h-6" />
                </Link>
                <Link
                  to="https://www.facebook.com/profile.php?id=61581198987271"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaFacebook className="w-6 h-6" />
                </Link>
                <Link
                  to="https://www.youtube.com/@SapiensAI-Tech"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaYoutube className="w-6 h-6" />
                </Link>
                <Link
                  to="https://www.instagram.com/sapiensai.tech/"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaInstagram className="w-6 h-6" />
                </Link>
                <Link
                  to="https://www.tiktok.com/@sapiens_ai_tech"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaTiktok className="w-6 h-6" />
                </Link>
                <Link
                  to="#"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaTwitter className="w-6 h-6" />
                </Link>
                
                <Link
                  to="#"
                  target="_blank"
                  className="text-accent-600/90 hover:text-accent-600"
                >
                  <FaTelegram className="w-6 h-6" />
                </Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#about"
                    onClick={(e) => {
                      e.preventDefault();
                      const section = document.getElementById("about");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  >
                    About
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className=" text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  ></a>
                </li>
                <li>
                  <a
                    href="#"
                    className=" text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  ></a>
                </li>
                <li>
                  <a
                    href="#"
                    className=" text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  ></a>
                </li>
                <li>
                  <a
                    href="#"
                    className=" text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  >
                    Locations
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-4">Support</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#support"
                    onClick={(e) => {
                      e.preventDefault();
                      const section = document.getElementById("support");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  >
                    Contact
                  </a>
                </li>

                <li>
                  <a
                    href="#faq"
                    onClick={(e) => {
                      e.preventDefault();
                      const section = document.getElementById("faq");
                      if (section) {
                        section.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:text-accent-500"
                  >
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">FAQs</h4>
              <ul className="space-y-2">
                {faqs.slice(0, 2).map((faq, index) => (
                  <li key={index}>
                    <Link
                      to="#"
                      className="text-gray-600 text-sm dark:text-gray-400 hover:text-accent-500"
                    >
                      {faq.question}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {showDemoModal && <ShowDemoModal
        isOpen={true}
        onClose={() => setShowDemoModal(false)}
        caption="Watch our demo to see how Sapians-AI can transform your interview preparation with AI-powered mock interviews and real-time feedback."
      />}

    </div>
  );
}
